import { useEffect, useMemo, useState } from "react";
import { Check, Info } from "@phosphor-icons/react";

/**
 * ColorSizePicker
 * ---------------
 * Replaces the flat variant chip list with two separate pickers:
 *   1) Clickable color thumbnails (actual product image per color, not a swatch)
 *   2) Size buttons filtered to what's actually available in the picked color
 *
 * Behavior:
 * - The first available color is preselected so the customer sees a live hero image.
 * - NO size is preselected — the customer must pick one before Add to Cart.
 * - Sizes that are out of stock for the current color are disabled + labelled.
 * - If a product has no color OR no size dimension, we degrade gracefully:
 *     • Only colors → just a color grid, no size row
 *     • Only sizes → just a size grid
 *     • Neither → single variant, no picker
 *
 * Props:
 *   variants: Printful variants array (must include color, size, availability_status, preview_url)
 *   value: currently selected variant object (owned by parent so it can share w/ hero image + cart)
 *   onChange(variant | null): called when the effective (color,size) pair maps to a single variant
 *   onValidityChange(bool): notifies parent whether "add to cart" is allowed
 */
export default function ColorSizePicker({ variants, value, onChange, onValidityChange, sizeGuideUrl, onOpenSizeGuide }) {
  const list = Array.isArray(variants) ? variants : [];

  // Group by color → array of {color, sizes: [{variant, size, out}], sample}
  const colorGroups = useMemo(() => {
    const map = new Map();
    for (const v of list) {
      const c = v.color || "One color";
      if (!map.has(c)) map.set(c, { color: c, sizes: [], sample: v });
      map.get(c).sizes.push({ variant: v, size: v.size || "One size", out: v.availability_status && v.availability_status !== "active" });
    }
    // Preserve first-seen order
    return Array.from(map.values());
  }, [list]);

  const hasColorAxis = colorGroups.length > 1;
  const hasSizeAxis = colorGroups.some((g) => g.sizes.length > 1);

  // Local state — synced back to parent through effects
  const initialColor = value?.color || colorGroups.find((g) => g.sizes.some((s) => !s.out))?.color || colorGroups[0]?.color || null;
  const [color, setColor] = useState(initialColor);
  const [size, setSize] = useState(value?.color === initialColor ? value?.size || null : null);

  // Sizes available for the currently picked color
  const currentGroup = colorGroups.find((g) => g.color === color);
  const currentSizes = currentGroup?.sizes || [];

  // Effective variant = the single variant matching (color, size)
  const effective = useMemo(() => {
    if (!currentGroup) return null;
    if (!hasSizeAxis) return currentGroup.sizes[0]?.variant || null;
    if (!size) return null;
    const hit = currentSizes.find((s) => s.size === size);
    return hit && !hit.out ? hit.variant : null;
  }, [color, size, currentGroup, currentSizes, hasSizeAxis]);

  // Push the effective variant up to the parent
  useEffect(() => {
    onChange?.(effective);
  }, [effective]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validity: for products WITH a size axis, both color + size must be picked
  useEffect(() => {
    const valid = hasSizeAxis
      ? Boolean(color && size && effective)
      : Boolean(effective);
    onValidityChange?.(valid);
  }, [color, size, effective, hasSizeAxis]); // eslint-disable-line react-hooks/exhaustive-deps

  // If picking a new color makes the current size unavailable, clear it (user must reselect)
  useEffect(() => {
    if (!size) return;
    const hit = currentSizes.find((s) => s.size === size);
    if (!hit || hit.out) setSize(null);
  }, [color]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single-variant product → hide the picker BUT still emit the variant + valid
  // to the parent so Add to Cart isn't stuck disabled.
  if (list.length <= 1 && !hasColorAxis && !hasSizeAxis) {
    return <SingleVariantBridge variant={list[0] || null} onChange={onChange} onValidityChange={onValidityChange} />;
  }

  return (
    <div className="space-y-5">
      {/* Color thumbnails */}
      {hasColorAxis && (
        <div data-testid="color-picker">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70">
              Color <span className="text-white ml-1">· {color || "—"}</span>
            </p>
            <p className="text-white/40 text-[10px]">{colorGroups.length} options</p>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory">
            {colorGroups.map((g) => {
              const allOut = g.sizes.every((s) => s.out);
              const active = g.color === color;
              return (
                <button
                  key={g.color}
                  type="button"
                  onClick={() => setColor(g.color)}
                  data-testid={`color-swatch-${g.color}`}
                  aria-label={`Color: ${g.color}${allOut ? " (out of stock)" : ""}`}
                  aria-pressed={active}
                  className={`relative flex-shrink-0 snap-start w-20 h-20 md:w-24 md:h-24 border-2 bg-white overflow-hidden transition-colors ${
                    active ? "border-[#B32027]" : "border-white/15 hover:border-white/45"
                  } ${allOut ? "opacity-50" : ""}`}
                >
                  {g.sample?.preview_url ? (
                    <img src={g.sample.preview_url} alt={g.color} loading="lazy" className="w-full h-full object-contain" />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[10px] font-bold uppercase text-black/60">{g.color}</span>
                  )}
                  {active && (
                    <span className="absolute top-1 right-1 bg-[#B32027] text-white w-5 h-5 flex items-center justify-center">
                      <Check size={11} weight="bold" />
                    </span>
                  )}
                  {allOut && (
                    <span className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[9px] uppercase tracking-[0.15em] font-bold py-1 text-center">
                      Sold out
                    </span>
                  )}
                  <span className="sr-only">{g.color}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Size buttons */}
      {hasSizeAxis && (
        <div data-testid="size-picker">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70">
              Size {size && <span className="text-white ml-1">· {size}</span>}
              {!size && <span className="text-[#B32027] ml-1 normal-case tracking-normal">(pick one)</span>}
            </p>
            {(onOpenSizeGuide || sizeGuideUrl) && (
              onOpenSizeGuide ? (
                <button
                  type="button"
                  onClick={onOpenSizeGuide}
                  data-testid="size-guide-link"
                  className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 hover:text-[#B32027] inline-flex items-center gap-1 underline decoration-white/25 hover:decoration-[#B32027]"
                >
                  <Info size={11} weight="bold" /> Size guide
                </button>
              ) : (
                <a href={sizeGuideUrl} target="_blank" rel="noreferrer noopener" data-testid="size-guide-link" className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70 hover:text-[#B32027] inline-flex items-center gap-1">
                  <Info size={11} weight="bold" /> Size guide
                </a>
              )
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {currentSizes.map((s) => {
              const active = size === s.size && !s.out;
              return (
                <button
                  key={s.size}
                  type="button"
                  disabled={s.out}
                  onClick={() => setSize(s.size)}
                  data-testid={`size-btn-${s.size}`}
                  aria-pressed={active}
                  className={`min-w-[52px] text-sm font-bold uppercase tracking-wider px-4 py-3 border-2 transition-colors ${
                    active
                      ? "border-[#B32027] bg-[#B32027]/10 text-white"
                      : s.out
                      ? "border-white/10 text-white/25 line-through cursor-not-allowed"
                      : "border-white/25 text-white/80 hover:border-white/60"
                  }`}
                >
                  {s.size}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SingleVariantBridge({ variant, onChange, onValidityChange }) {
  useEffect(() => {
    onChange?.(variant || null);
    onValidityChange?.(Boolean(variant));
  }, [variant]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
